/**
 * The check-in offer that appears when you pick something to focus on.
 *
 * OWNER ASK 2026-08-30
 * --------------------
 * "When picking up an item, we should actually display an option to add an
 *  optional reminder with necessary fields — when do you want to complete this
 *  by, when do you want me to start checking on you, if you want that to
 *  encroach into the quiet time space... This should be common to anything
 *  that is picked for 'Focus' from the app. Fill in some known defaults."
 *
 * OPTIONAL IS LOAD-BEARING. "Start now" is the primary button and needs no
 * decisions; the fields are pre-filled and the panel can be ignored entirely.
 * A focus flow that demands four answers before you may begin is a worse
 * product than one that just starts, and this page's whole thesis is to lower
 * the heart rate before asking for anything.
 *
 * IT WRITES TASK FIELDS, NOT A CONTRACT. Saving sets due_date / due_time /
 * hold_lead_minutes / hold_interval_minutes on the task, and the reminder
 * sweep arms the contract itself when the lead window opens
 * (src/domains/reminders/deadline_arming.py, shipped this morning). Creating a
 * contract directly here would produce a second, unbound one alongside it —
 * the task already carries the deadline, so the task is where this belongs.
 *
 * DOCTRINE. A deadline the user sets is FIDELITY: never capped, never
 * occasion-judged. Quiet hours are the one exception the user must opt into
 * deliberately, which is why that control is a switch that defaults to off and
 * says plainly what it will do.
 */
import { useMemo, useState } from 'react'
import { AlarmClock, ArrowRight, Moon, X } from 'lucide-react'
import { BRAND } from '../../config/brand'

export interface FocusTarget {
  id: string | number
  title: string
  /** Minutes, when the item carries an estimate. */
  estimatedDuration?: number | null
  /** "HH:MM" or an ISO datetime — whatever the item was scheduled for. */
  scheduledTimeSlot?: string | null
  /** "YYYY-MM-DD" if the item already has a due date. */
  dueDate?: string | null
}

export interface FocusPlan {
  due_date: string
  due_time: string
  hold_lead_minutes: number
  hold_interval_minutes: number
  hold_quiet_hours_mode: 'respect' | 'sovereign'
}

const pad = (n: number) => String(n).padStart(2, '0')
const hhmm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/** Round up to the next quarter hour — nobody sets a deadline of 14:07. */
function roundUp(d: Date, stepMinutes = 15): Date {
  const out = new Date(d)
  out.setSeconds(0, 0)
  const over = out.getMinutes() % stepMinutes
  if (over !== 0) out.setMinutes(out.getMinutes() + (stepMinutes - over))
  return out
}

function parseClock(value: string | null | undefined, onDay: Date): Date | null {
  if (!value) return null
  const m = /(\d{1,2}):(\d{2})/.exec(value)
  if (!m) return null
  const out = new Date(onDay)
  out.setHours(Number(m[1]), Number(m[2]), 0, 0)
  return out
}

/**
 * The defaults, in the order the owner described them.
 *
 * "You might know when the next item in the schedule will start and therefore
 *  know when this one should end, and maybe start from 45 mins or 1 hour
 *  onwards — whatever is realistically good."
 *
 * So: finish by the next scheduled thing if there is one (never run this into
 * the next commitment), otherwise by its own estimate, otherwise an hour. The
 * lead is 45 minutes but never longer than the window itself — a 20-minute
 * window must not start nagging before it begins.
 */
export function defaultPlan(target: FocusTarget, nextScheduledAt?: string | null, now = new Date()): FocusPlan {
  const today = new Date(now)
  const nextStart = parseClock(nextScheduledAt, today)

  let end: Date
  if (nextStart && nextStart.getTime() > now.getTime() + 5 * 60000) {
    end = nextStart                                   // finish before the next commitment
  } else if (target.estimatedDuration && target.estimatedDuration > 0) {
    end = roundUp(new Date(now.getTime() + target.estimatedDuration * 60000))
  } else {
    end = roundUp(new Date(now.getTime() + 60 * 60000))
  }

  const windowMinutes = Math.max(5, Math.round((end.getTime() - now.getTime()) / 60000))
  const lead = Math.min(45, windowMinutes)
  // Cadence scaled to the window: a 20-minute job nagged every 15 minutes is
  // noise; a three-hour one checked every 5 is worse.
  const interval = windowMinutes <= 30 ? 5 : windowMinutes <= 90 ? 15 : 30

  return {
    due_date: target.dueDate || ymd(end),
    due_time: hhmm(end),
    hold_lead_minutes: lead,
    hold_interval_minutes: interval,
    hold_quiet_hours_mode: 'respect',
  }
}

/** Write the plan onto the TASK, not into a contract.
 *
 * The reminder sweep arms the contract itself once the lead window opens
 * (src/domains/reminders/deadline_arming.py). Creating one here would leave a
 * second, unbound contract beside the one the task already implies — and the
 * strip would then hold two rows for one job.
 *
 * Fails soft on purpose: never block someone from starting their work because
 * the reminder could not be saved.
 */
export async function saveFocusPlan(taskId: string | number, plan: FocusPlan): Promise<boolean> {
  try {
    const r = await fetch(`/api/hierarchy/tasks/${taskId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...plan,
        deadline_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      }),
    })
    return r.ok
  } catch {
    return false
  }
}

const INTERVALS = [5, 10, 15, 30, 60]
const LEADS = [15, 30, 45, 60, 90]

export function FocusSetup({ target, nextScheduledAt, onStart, onCancel, busy }: {
  target: FocusTarget
  /** The next scheduled item's time, so this one can be told to end before it. */
  nextScheduledAt?: string | null
  /** plan === null means "just start, no check-ins". */
  onStart: (plan: FocusPlan | null) => void
  onCancel: () => void
  busy?: boolean
}) {
  const initial = useMemo(() => defaultPlan(target, nextScheduledAt), [target, nextScheduledAt])
  const [plan, setPlan] = useState<FocusPlan>(initial)
  const [remind, setRemind] = useState(false)

  const set = <K extends keyof FocusPlan>(k: K, v: FocusPlan[K]) => setPlan(p => ({ ...p, [k]: v }))

  return (
    <div className="rounded-2xl border border-brand-subtle bg-surface p-4 shadow-sm space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-app-body font-bold text-primary">Starting: {target.title}</h3>
          <p className="text-app-micro text-muted mt-0.5">
            Want {BRAND.assistant} to check on you while you do it? Entirely optional.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          className="shrink-0 rounded-lg p-1 text-muted hover:bg-surface-alt hover:text-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {!remind ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => onStart(null)}
            className="inline-flex min-h-touch items-center gap-1.5 rounded-xl bg-brand px-4 text-app-meta font-semibold text-on-brand hover:opacity-90 disabled:opacity-60"
          >
            Start now <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setRemind(true)}
            className="inline-flex min-h-touch items-center gap-1.5 rounded-xl border border-soft px-3 text-app-meta font-medium text-primary hover:bg-surface-alt"
          >
            <AlarmClock className="h-3.5 w-3.5 text-warn" /> Hold me to it
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="min-w-0">
              <span className="block text-app-micro font-bold uppercase tracking-widest text-muted">
                Finish by
              </span>
              <div className="mt-1 flex gap-2">
                <input
                  type="date"
                  value={plan.due_date}
                  onChange={e => set('due_date', e.target.value)}
                  className="min-h-touch min-w-0 flex-1 rounded-xl border border-soft bg-surface px-2 text-app-meta text-primary"
                />
                <input
                  type="time"
                  value={plan.due_time}
                  onChange={e => set('due_time', e.target.value)}
                  className="min-h-touch w-28 rounded-xl border border-soft bg-surface px-2 text-app-meta text-primary"
                />
              </div>
              <span className="mt-1 block text-app-micro text-muted">
                {nextScheduledAt
                  ? `Set to end before your next scheduled thing at ${nextScheduledAt}.`
                  : target.estimatedDuration
                    ? `Based on your ${target.estimatedDuration}-minute estimate.`
                    : 'An hour from now, rounded.'}
              </span>
            </label>

            <label className="min-w-0">
              <span className="block text-app-micro font-bold uppercase tracking-widest text-muted">
                Start checking on me
              </span>
              <select
                value={plan.hold_lead_minutes}
                onChange={e => set('hold_lead_minutes', Number(e.target.value))}
                className="mt-1 min-h-touch w-full rounded-xl border border-soft bg-surface px-2 text-app-meta text-primary"
              >
                {LEADS.map(m => (
                  <option key={m} value={m}>{m} minutes before</option>
                ))}
              </select>
              <span className="mt-1 block text-app-micro text-muted">
                Nothing before then — it stays quiet until the window opens.
              </span>
            </label>

            <label className="min-w-0">
              <span className="block text-app-micro font-bold uppercase tracking-widest text-muted">
                How often
              </span>
              <select
                value={plan.hold_interval_minutes}
                onChange={e => set('hold_interval_minutes', Number(e.target.value))}
                className="mt-1 min-h-touch w-full rounded-xl border border-soft bg-surface px-2 text-app-meta text-primary"
              >
                {INTERVALS.map(m => (
                  <option key={m} value={m}>every {m} minutes</option>
                ))}
              </select>
              <span className="mt-1 block text-app-micro text-muted">
                It keeps going past the deadline until you mark it done.
              </span>
            </label>

            <div className="min-w-0">
              <span className="block text-app-micro font-bold uppercase tracking-widest text-muted">
                Quiet hours
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={plan.hold_quiet_hours_mode === 'sovereign'}
                onClick={() => set('hold_quiet_hours_mode',
                  plan.hold_quiet_hours_mode === 'sovereign' ? 'respect' : 'sovereign')}
                className={`mt-1 inline-flex min-h-touch w-full items-center gap-2 rounded-xl border px-3 text-left text-app-meta font-medium transition-colors ${
                  plan.hold_quiet_hours_mode === 'sovereign'
                    ? 'border-warn bg-warn/10 text-warn'
                    : 'border-soft bg-surface text-primary hover:bg-surface-alt'
                }`}
              >
                <Moon className="h-3.5 w-3.5 shrink-0" />
                {plan.hold_quiet_hours_mode === 'sovereign' ? 'Wake me if it comes to that' : 'Stay quiet at night'}
              </button>
              <span className="mt-1 block text-app-micro text-muted">
                {plan.hold_quiet_hours_mode === 'sovereign'
                  ? 'This one may reach you during your quiet hours.'
                  : 'Held until your quiet hours end.'}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => onStart(plan)}
              className="inline-flex min-h-touch items-center gap-1.5 rounded-xl bg-brand px-4 text-app-meta font-semibold text-on-brand hover:opacity-90 disabled:opacity-60"
            >
              {busy ? 'Starting…' : 'Start, and hold me to it'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onStart(null)}
              className="inline-flex min-h-touch items-center rounded-xl border border-soft px-3 text-app-meta font-medium text-secondary hover:bg-surface-alt"
            >
              Just start
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default FocusSetup
